import { useState } from 'react';
import { Box, Container, Stack, IconButton, Drawer, Link } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useResponsive } from 'src/hooks/use-responsive';
import { RouterLink } from 'src/routes/components';
import { usePathname } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { _socials } from 'src/_mock';

// Helper function to get nav link color
function getNavLinkColor(showSolidBg: boolean, isActive: boolean): string {
  if (showSolidBg) {
    return isActive ? 'primary.darker' : 'text.primary';
  }
  return isActive ? 'primary.darker' : 'common.white';
}

// ----------------------------------------------------------------------

const NAV_ITEMS = [
  { label: 'Home', labelAr: 'الرئيسية', href: paths.home },
  { label: 'Services', labelAr: 'خدماتنا', href: paths.category.root },
  { label: 'About', labelAr: 'من نحن', href: paths.about },
  { label: 'Contact', labelAr: 'تواصل معنا', href: paths.contact },
];

// ----------------------------------------------------------------------

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = usePathname();
  const { isRTL } = useLocales();
  const mdUp = useResponsive('up', 'md');

  // Keep grey background with opacity all the time
  const showSolidBg = false;

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backgroundColor: 'primary.main',
          backdropFilter: 'blur(6px)',
        }}
      >
        <Container maxWidth={false}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ height: 80, px: { xs: 0, md: 2 } }}
          >
            {/* Logo on Left */}
            <Link component={RouterLink} href="/" sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                alt="logo"
                src="/logo/wlogo.png"
                sx={{
                  height: { xs: 35, md: 45 },
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </Link>

            {/* Right side: Nav links + Menu Icon */}
            <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 3 }}>
              {/* Desktop Nav Links */}
              {mdUp && (
                <Stack direction="row-reverse" spacing={4}>
                  {NAV_ITEMS.map((item) => {
                    const isActive =
                      pathname === item.href ||
                      (item.href !== '/' && pathname.startsWith(item.href));
                    return (
                      <Link
                        key={item.label}
                        component={RouterLink}
                        href={item.href}
                        sx={{
                          fontSize: 15,
                          fontWeight: isActive ? 600 : 500,
                          color: getNavLinkColor(showSolidBg, isActive),
                          textDecoration: 'none',
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: 'primary.darker',
                          },
                        }}
                      >
                        {isRTL ? item.labelAr : item.label}
                      </Link>
                    );
                  })}
                </Stack>
              )}
              {/* Menu Icon - Opens Drawer */}
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: 'common.white',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.darker',
                  },
                }}
              >
                <Iconify icon="solar:hamburger-menu-linear" width={28} />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Drawer Menu - Opens from Right */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 400 },
            bgcolor: 'background.default',
            borderRadius: 0,
          },
        }}
      >
        <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 6 }}>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <Iconify icon="mingcute:close-line" width={28} />
            </IconButton>
          </Box>

          {/* Menu Items */}
          <Stack spacing={3} sx={{ flex: 1 }}>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                component={RouterLink}
                href={item.href}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 300,
                  color: 'text.primary',
                  textDecoration: 'none',
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 0.6,
                  },
                }}
              >
                {isRTL ? item.labelAr : item.label}
              </Link>
            ))}
          </Stack>

          {/* Social Links */}
          <Stack direction="row" spacing={2} sx={{ mt: 'auto', pt: 4 }}>
            {_socials.map((social) => (
              <IconButton
                key={social.name}
                href={social.path}
                target="_blank"
                sx={{ color: 'text.primary' }}
              >
                <Iconify icon={social.icon} width={24} />
              </IconButton>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
